/**
 * Payment Pages Event Manager
 *
 * Pre-configured EventManager instance with GarfieldLogger and FoxtrotLogger.
 * Both loggers receive events from a single addEvent() call.
 * Provides typed methods for different event types (custom, click, pageview, error).
 */

import { EventManager } from '@shared/services/eventManager';

import { createGarfieldLogger } from './GarfieldLogger.service';
import { createFoxtrotLogger } from './FoxtrotLogger.service';

/** Base EventManager instance */
const baseEventManager = EventManager.get();

/** Event data with optional Garfield metadata */
export interface IEventData {
	value?: unknown;
	garfieldData?: Record<string, unknown>;
	[key: string]: unknown;
}

/**
 * Payment Pages Event Manager
 *
 * Wrapper around base EventManager with typed methods for different event types.
 * Each method automatically sets the correct eventType for Garfield.
 */
export const eventManager = {
	addEvent: (eventName: string, data?: IEventData): void => {
		baseEventManager.addEvent(eventName, {
			...data,
			eventType: 'custom',
		});
	},

	addClickEvent: (eventName: string, data?: IEventData): void => {
		baseEventManager.addEvent(eventName, {
			...data,
			eventType: 'click',
		});
	},

	addPageViewEvent: (eventName: string, data?: IEventData): void => {
		baseEventManager.addEvent(eventName, {
			...data,
			eventType: 'pageview',
		});
	},

	addErrorEvent: (eventName: string, data?: IEventData): void => {
		baseEventManager.addEvent(eventName, {
			...data,
			eventType: 'apiError',
		});
	},

	pushCriticalEvent: (eventName: string, data?: IEventData): void => {
		baseEventManager.pushCriticalEvents(eventName, {
			...data,
			eventType: 'click',
		});
	},

	flushEvents: (): void => {
		baseEventManager.flushEvents();
	},

	flushEventsAsync: (): Promise<void> => baseEventManager.flushEventsAsync(),

	register: baseEventManager.register.bind(baseEventManager),

	setEnabled: baseEventManager.setEnabled.bind(baseEventManager),
};

export interface InitEventManagerOptions {
	meta?: Record<string, unknown>;
	disableGarfield?: boolean;
	disableFoxtrot?: boolean;
}

/**
 * Initialize with GarfieldLogger and FoxtrotLogger - call once during app startup
 */
export const initEventManager = (options?: InitEventManagerOptions): void => {
	const {
		meta,
		disableGarfield = false,
		disableFoxtrot = false,
	} = options ?? {};

	// Register GarfieldLogger for analytics tracking
	if (!disableGarfield) {
		baseEventManager.register(
			createGarfieldLogger({
				id: 'garfield',
				source: 'PAYLINK',
				meta,
			}),
		);
	}

	// Register FoxtrotLogger for backend event tracking
	if (!disableFoxtrot) {
		baseEventManager.register(
			createFoxtrotLogger({
				id: 'foxtrot',
				source: 'PAYLINK',
				meta,
			}),
		);
	}
};

/** Enable or disable Garfield logger at runtime */
export const setGarfieldLoggingEnabled = (enabled: boolean): void => {
	baseEventManager.setEnabled('garfield', enabled);
};

/** Enable or disable Foxtrot logger at runtime */
export const setFoxtrotLoggingEnabled = (enabled: boolean): void => {
	baseEventManager.setEnabled('foxtrot', enabled);
};
